import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { COUNTRIES, getStatesForCountry, getCitiesForState } from '@alascorporation/utils';
import { traAdmin, type TraCertificationEdition } from '@/lib/api/traAdminClient';
import { getErrorMessage } from '../../lib/errors';
import ModuleLayout from '../../components/admin/ModuleLayout';
import { traNavTabs } from '@/components/tra/traNavTabs';
import { inputCls, labelCls, textareaCls, btnSave, btnSecondary, CheckRow, StyledSelect } from '../../components/admin/FormComponents';

const emptyForm = {
  editionId: '',
  fullName: '',
  countryCode: 'CO',
  stateProvince: '',
  city: '',
  educationalInstitution: '',
  degreeOrProgram: '',
  cedula: '',
  dateOfBirth: '',
  address: '',
  email: '',
  phone: '',
  rocketryExperience: '',
  motivation: '',
  referredBy: '',
  isExistingTraMember: false,
  traMemberNumber: '',
};

// Alta manual de un interesado que no se registró por el sitio público de
// Tripoli (tra-edition-tabbed-detail) — página completa, no modal, para
// mantener consistencia con el resto de formularios de creación de TRA/CCCD.
export default function AddWaitlistEntryPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();
  const fixedEditionId = searchParams.get('editionId') ?? undefined;

  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');
  const [editions, setEditions] = useState<TraCertificationEdition[]>([]);

  const states = getStatesForCountry(form.countryCode);
  const selectedState = states.find((s) => s.value === form.stateProvince);
  const cities = getCitiesForState(form.countryCode, selectedState?.isoCode);

  useEffect(() => {
    if (fixedEditionId) return;
    traAdmin
      .listEditions({ limit: 50, page: 1 })
      .then((data) => setEditions(data.items ?? []))
      .catch(() => setEditions([]));
  }, [fixedEditionId]);

  function goBack() {
    navigate(fixedEditionId ? `/tra/editions/${fixedEditionId}?tab=participantes` : '/tra/waitlist');
  }

  const mutation = useMutation({
    mutationFn: () =>
      traAdmin.createWaitlistEntryAdmin({
        ...form,
        editionId: fixedEditionId ?? form.editionId,
        rocketryExperience: form.rocketryExperience || undefined,
        referredBy: form.referredBy || undefined,
        traMemberNumber: form.isExistingTraMember ? form.traMemberNumber : undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['server-table', '/tra/certification-waitlist'] });
      goBack();
    },
    onError: (err) => setError(getErrorMessage(err)),
  });

  function f(key: keyof typeof emptyForm) {
    return {
      value: form[key] as string,
      onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
        setForm((prev) => ({ ...prev, [key]: e.target.value })),
    };
  }

  return (
    <ModuleLayout
      breadcrumbs={[
        { label: 'SateLab', to: '/' },
        'TRA',
        fixedEditionId
          ? { label: 'Ediciones', to: '/tra/editions' }
          : { label: 'Lista de espera', to: '/tra/waitlist' },
        'Agregar interesado',
      ]}
      title="Agregar interesado manualmente"
      subtitle="Para candidatos que no se registraron por el sitio público de Tripoli Colombia"
      tabs={traNavTabs(fixedEditionId ? 'editions' : 'waitlist')}
      metrics={[]}
    >
      <form
        onSubmit={(e: FormEvent<HTMLFormElement>) => {
          e.preventDefault();
          mutation.mutate();
        }}
      >
        <div className="space-y-6 rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
          {!fixedEditionId && (
            <div>
              <label className={labelCls}>Edición *</label>
              <StyledSelect value={form.editionId} onChange={(e) => setForm((f) => ({ ...f, editionId: e.target.value }))}>
                <option value="">Selecciona una edición</option>
                {editions.map((ed) => (
                  <option key={ed.id} value={ed.id}>{ed.name}</option>
                ))}
              </StyledSelect>
            </div>
          )}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className={labelCls}>Nombre completo *</label>
              <input required autoFocus className={inputCls} {...f('fullName')} />
            </div>
            <div>
              <label className={labelCls}>Cédula *</label>
              <input required pattern="\d{6,10}" title="6 a 10 dígitos" className={inputCls} {...f('cedula')} />
            </div>
            <div>
              <label className={labelCls}>Correo electrónico *</label>
              <input required type="email" className={inputCls} {...f('email')} />
            </div>
            <div>
              <label className={labelCls}>Teléfono *</label>
              <input required className={inputCls} {...f('phone')} />
            </div>
            <div>
              <label className={labelCls}>País *</label>
              <StyledSelect
                value={form.countryCode}
                onChange={(e) => setForm((prev) => ({ ...prev, countryCode: e.target.value, stateProvince: '', city: '' }))}
              >
                {COUNTRIES.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </StyledSelect>
            </div>
            <div>
              <label className={labelCls}>Departamento / Estado *</label>
              {states.length > 0 ? (
                <StyledSelect
                  value={form.stateProvince}
                  onChange={(e) => setForm((prev) => ({ ...prev, stateProvince: e.target.value, city: '' }))}
                >
                  <option value="">Selecciona...</option>
                  {states.map((s) => (
                    <option key={s.isoCode} value={s.value}>{s.label}</option>
                  ))}
                </StyledSelect>
              ) : (
                <input required className={inputCls} {...f('stateProvince')} />
              )}
            </div>
            <div>
              <label className={labelCls}>Ciudad *</label>
              {cities.length > 0 ? (
                <StyledSelect value={form.city} onChange={(e) => setForm((prev) => ({ ...prev, city: e.target.value }))}>
                  <option value="">Selecciona...</option>
                  {cities.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </StyledSelect>
              ) : (
                <input required className={inputCls} disabled={states.length > 0 && !selectedState} {...f('city')} />
              )}
            </div>
            <div>
              <label className={labelCls}>Fecha de nacimiento *</label>
              <input required type="date" className={inputCls} {...f('dateOfBirth')} />
            </div>
            <div>
              <label className={labelCls}>Institución educativa *</label>
              <input required className={inputCls} {...f('educationalInstitution')} />
            </div>
            <div>
              <label className={labelCls}>Título o programa *</label>
              <input required className={inputCls} {...f('degreeOrProgram')} />
            </div>
          </div>

          <div>
            <label className={labelCls}>Dirección *</label>
            <input required className={inputCls} {...f('address')} />
          </div>

          <div>
            <label className={labelCls}>Motivación * (mínimo 20 caracteres)</label>
            <textarea required minLength={20} rows={2} className={textareaCls} {...f('motivation')} />
          </div>

          <div>
            <label className={labelCls}>Experiencia en cohetería (opcional)</label>
            <textarea rows={2} className={textareaCls} {...f('rocketryExperience')} />
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className={labelCls}>Referido por (opcional)</label>
              <input className={inputCls} {...f('referredBy')} />
            </div>
            <div className="flex items-end pb-2">
              <CheckRow
                checked={form.isExistingTraMember}
                onChange={() =>
                  setForm((f) => ({ ...f, isExistingTraMember: !f.isExistingTraMember, traMemberNumber: '' }))
                }
                label="Ya es miembro TRA"
              />
            </div>
          </div>

          {form.isExistingTraMember && (
            <div>
              <label className={labelCls}>Número de miembro TRA *</label>
              <input required className={inputCls} {...f('traMemberNumber')} />
            </div>
          )}
        </div>

        {error ? (
          <p role="alert" aria-live="assertive" className="mt-5 text-sm font-medium text-error-500">
            {error}
          </p>
        ) : null}

        <div className="mt-5 flex justify-end gap-2 border-t border-gray-100 pt-5 dark:border-gray-800">
          <button type="button" onClick={goBack} className={btnSecondary}>Cancelar</button>
          <button type="submit" disabled={mutation.isPending} className={btnSave}>
            {mutation.isPending ? 'Guardando...' : 'Agregar interesado'}
          </button>
        </div>
      </form>
    </ModuleLayout>
  );
}
